import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Car } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import ShipmentChatPanel from "./ShipmentChatPanel";
import ShipmentDetailDrawer from "./ShipmentDetailDrawer";
import ShipmentsEmptyState from "@/components/shipments/ShipmentsEmptyState";
import { useIntersectionLoadMore } from "@/hooks/useIntersectionLoadMore";
import { ShipmentListLoadingSkeleton } from "@/features/shipments/ShipmentListItemSkeleton";
import MobileIconButton from "@/components/MobileIconButton";
import { ShipmentListFilterBar, useShipmentListFilters } from "@/features/shipments/ShipmentListFilters";
import StartShipmentButton from "@/components/shipments/StartShipmentButton";
import { goToNewShipmentChat } from "@/utils/startNewShipment";

type View = "list" | "chat";

export default function MobileShipmentsView({
  shipments,
  isLoading,
  onMenuClick,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: {
  shipments: any[];
  isLoading: boolean;
  onMenuClick?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const navigate = useNavigate();
  const [view, setView] = useState<View>("list");
  const [selected, setSelected] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef(0);

  const {
    search,
    setSearch,
    status,
    setStatus,
    dateSort,
    setDateSort,
    statusOptions,
    filtered,
  } = useShipmentListFilters(shipments);

  const loadMoreSentinelRef = useIntersectionLoadMore({
    enabled: hasMore && !isLoading,
    isLoading: isLoadingMore,
    onLoadMore,
    rootRef: listRef,
    observeKey: shipments.length,
    resetKey: selected?.id,
  });

  const handleSelectShipment = (shipment: any) => {
    listScrollRef.current = listRef.current?.scrollTop ?? 0;
    setSelected(shipment);
    setView("chat");
  };

  // ── List ──────────────────────────────────────────────────────────────────
  if (view === "list") {
    return (
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="grid grid-cols-[36px_1fr_auto] items-center gap-2 px-4 py-3 border-b border-[#E4E7EC]">
          <MobileIconButton onClick={onMenuClick} aria-label="Open menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </MobileIconButton>
          <h1 className="text-[18px] font-[600] text-[#1D2739] text-center min-w-0">Shipments</h1>
          <StartShipmentButton
            label="New"
            className="h-9 min-h-[36px] max-h-9 justify-self-end px-3 text-sm"
            onClick={() => goToNewShipmentChat(navigate)}
          />
        </div>

        {/* Body */}
        {isLoading && shipments.length === 0 ? (
          <div className="flex-1 overflow-y-auto px-3 pt-3">
            <ShipmentListLoadingSkeleton rows={4} />
          </div>
        ) : shipments.length === 0 ? (
          <ShipmentsEmptyState className="flex-1" onStartShipment={() => goToNewShipmentChat(navigate)} />
        ) : (
          <div className="flex-1 overflow-y-auto" ref={listRef}>
            <div className="px-4 py-3 border-b border-[#E4E7EC]">
              <ShipmentListFilterBar
                search={search}
                onSearchChange={setSearch}
                status={status}
                onStatusChange={setStatus}
                dateSort={dateSort}
                onDateSortChange={setDateSort}
                statusOptions={statusOptions}
              />
            </div>

            <div className="px-3 pt-3 flex flex-col gap-3">
              {filtered.length === 0 ? (
                <ShipmentsEmptyState variant="no-results" query={search} />
              ) : null}
              {filtered.map((s) => (
                <div
                  key={s.id}
                  onClick={() => handleSelectShipment(s)}
                  className="bg-white border border-[#E4E7EC] rounded-[12px] p-4 cursor-pointer hover:bg-[#F9FAFB] transition-all"
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Car size={14} color="#475367" className="flex-shrink-0" />
                      <span className="text-[10px] text-[#475367] font-[600] truncate">
                        #{s.number}
                      </span>
                    </div>
                    <StatusBadge status={s.status} className="max-w-[44vw] sm:max-w-none" />
                  </div>
                  <h4 className="text-[14px] font-[600] text-[#1D2739] mb-3 truncate">{s.vehicle}</h4>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] text-[#344054] mb-1 truncate" title={s.from}>
                        {s.from}
                      </p>
                      <p className="text-[14px] font-[500] text-[#101928]">{s.date}</p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1 text-[11px] text-[#667185] self-center">
                      <span className="text-[#475367]">●- - -</span>
                      <span className="border border-[#D0D5DD] bg-[#F0F2F5] rounded-full px-2 py-0.5 text-[11px] text-[#344054] whitespace-nowrap">
                        ⏱ {s.eta}
                      </span>
                      <span className="text-[#475367]">- - -●</span>
                    </div>
                    <div className="text-right min-w-0 flex-1">
                      <p className="text-[12px] text-[#344054] mb-1 truncate" title={s.to}>
                        {s.to}
                      </p>
                      <p className="text-[14px] font-[500] text-[#101928]">{s.arrival}</p>
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length > 0 && hasMore ? <div ref={loadMoreSentinelRef} className="h-px" aria-hidden /> : null}
              {filtered.length > 0 && isLoadingMore ? <ShipmentListLoadingSkeleton rows={2} /> : null}
              {!hasMore && filtered.length >= 6 && !isLoadingMore ? (
                <p className="text-center text-[11px] text-[#98A2B3] py-3">You&apos;ve reached the end</p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Details view ── rendered as overlay on top of chat ─────────────────────
  if (view === "chat" && selected) {
    return (
      <div className="flex flex-col h-screen bg-white">
        <div className="flex-1 min-h-0 overflow-hidden">
          <ShipmentChatPanel
            shipment={selected}
            onOpenDetails={() => setDrawerOpen(true)}
            onBack={() => setView("list")}
          />
        </div>
        <ShipmentDetailDrawer
          isOpen={drawerOpen}
          shipment={selected}
          onClose={() => setDrawerOpen(false)}
        />
      </div>
    );
  }

  return null;
}
